import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { cardClass, focusRing, linkButtonClass } from "../Exams/TestBuilder/ui";
import { bookletHref, linkTapClass, sectionTitleClass, variantHref } from "./bts";
import { QuietButton } from "./print/ui";

// The issue codes with a text of their own (bts.issues.<code>); another code is named as it is.
const KNOWN = [
  "no_sections",
  "variant_numbers_invalid",
  "heading_missing",
  "variant_not_confirmed",
  "section_invalid",
  "question_changed",
  "section_not_variant",
  "section_other_folder",
  "section_other_grade",
  "section_other_stream",
  "duplicate_subject",
  "code_taken",
  "not_standard",
  "several_correct",
  "option_count",
  "option_reference",
  "changed_since_freeze",
  // A week of the matrix (of the variant's grade) without a topic. check_booklet sends it inside section_invalid's
  // codes (variant_issues); a booklet-level one gets this text and its variant's link too.
  "row_without_topic",
];

// A variant's blocking codes, each once: row_without_topic, row_short and row_extra come once per row and level.
export const variantIssueText = (t, codes) =>
  [...new Set(codes || [])].map((code) => t(`bts.variant_issues.${code}`, { defaultValue: code })).join("; ");

// Each issue once: a refused activation's check repeats changed_since_freeze as blocking next to the warning
// (the first one, the blocking one when they come first, stays).
export const uniqueIssues = (issues) => {
  const seen = new Set();
  return issues.filter((issue) => {
    const key = [issue.code, issue.variant || "", JSON.stringify(issue.codes || issue.variants || null)].join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// The section of the booklet's check whose variant is `uuid`.
const sectionOf = (sections, uuid) => (sections || []).find((s) => s.variant?.uuid === uuid) || null;

// The words of an issue (without its subject, shown before it). `active`: the booklet's, which prints as it was
// frozen (what would block an activation matters only before activating it again).
export function issueText(t, issue, sections, { active = false } = {}) {
  const count = issue.questions?.length || 0;
  switch (issue.code) {
    case "section_invalid":
      return t("bts.issues.section_invalid", { problems: variantIssueText(t, issue.issues) || "—" });
    case "question_changed":
      if (active) return issue.plan && !count ? t("bts.issues.plan_changed_active") : t("bts.issues.question_changed_active", { count });
      return issue.plan && !count ? t("bts.issues.plan_changed") : t("bts.issues.question_changed", { count });
    case "duplicate_subject":
      return t("bts.issues.duplicate_subject", {
        variants: (issue.variants || [])
          .map((uuid) => sectionOf(sections, uuid)?.variant?.name)
          .filter(Boolean)
          .map((name) => `«${name}»`)
          .join(", "),
      });
    case "code_taken":
      return t("bts.issues.code_taken", { count: issue.codes?.length || 0 });
    case "not_standard": {
      const planned = issue.planned || {};
      return t("bts.issues.not_standard", {
        count: planned.count ?? 0,
        a: planned.A ?? 0,
        b: planned.B ?? 0,
        c: planned.C ?? 0,
        points: planned.points ?? 0,
      });
    }
    case "several_correct":
    case "option_count":
    case "option_reference":
      return t(`bts.issues.${issue.code}`, { count });
    default:
      return KNOWN.includes(issue.code) ? t(`bts.issues.${issue.code}`) : t("bts.issues.unknown", { code: issue.code });
  }
}

/**
 * Issues of a booklet's check in plain words, each with its subject and a link to its variant (the bank's
 * variants tab, in a new tab: the page keeps what is typed in it); `code_taken` links the booklets that print
 * the codes. Blocking ones red, warnings amber. `active`: the check of an active booklet (see issueText).
 */
export function IssueList({ issues, sections, active = false, className = "" }) {
  const [t] = useTranslation();
  const location = useLocation();
  if (!issues?.length) return null;
  return (
    <ul className={`space-y-2 ${className}`}>
      {uniqueIssues(issues).map((issue, index) => {
        const section = sectionOf(sections, issue.variant);
        const subject = issue.subject_name || section?.variant?.folder?.name || "";
        const folderUuid = section?.variant?.folder?.uuid;
        const Icon = issue.blocking ? XCircleIcon : ExclamationTriangleIcon;
        return (
          <li
            key={`${issue.code}-${issue.variant || ""}-${index}`}
            className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-sm ${
              issue.blocking ? "border-red-200 bg-red-50/70 text-red-900" : "border-amber-200 bg-amber-50/70 text-amber-900"
            }`}
          >
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${issue.blocking ? "text-red-600" : "text-amber-600"}`} aria-hidden="true" />
            <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <div className="min-w-0 flex-1 break-words">
                <span className="sr-only">{issue.blocking ? t("bts.check.blocking_label") : t("bts.check.warning_label")}: </span>
                {subject && <span className="font-semibold">{subject} — </span>}
                {issueText(t, issue, sections, { active })}
                {issue.code === "not_standard" && issue.overridable && (
                  <span className="mt-0.5 block text-xs opacity-80">
                    {active ? t("bts.issues.not_standard_active") : t("bts.issues.not_standard_override")}
                  </span>
                )}
                {issue.code === "code_taken" && issue.codes?.length > 0 && (
                  <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    {issue.codes.map((item) => (
                      <li key={`${item.code}-${item.booklet?.uuid}`}>
                        <span className="font-mono tabular-nums">{item.code}</span>{" "}
                        {item.booklet?.uuid ? (
                          // The other booklet keeps the way back to the list as it was left.
                          <Link
                            to={bookletHref(item.booklet.uuid)}
                            state={location.state}
                            className={`rounded font-medium underline underline-offset-2 ${focusRing}`}
                          >
                            «{item.booklet.name}»
                          </Link>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {folderUuid && issue.variant && (
                <Link
                  to={variantHref(folderUuid, issue.variant)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${linkButtonClass} ${linkTapClass} -ml-1.5 -mt-1 w-fit shrink-0 whitespace-nowrap sm:-mr-1.5 sm:ml-0`}
                >
                  {issue.code === "question_changed" && !active ? t("bts.check.open_to_confirm") : t("bts.check.open_variant")}
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The booklet's check: whether it can be activated, its blocking issues and its warnings. An active booklet
 * prints as it was frozen: what would block an activation now is only noted, for activating it again.
 */
export default function BookletCheck({ booklet, onRefresh, refreshing }) {
  const [t] = useTranslation();
  const check = booklet.check || { ok: false, issues: [], sections: [] };
  const { status } = booklet;
  const draft = status === "draft";
  const active = status === "active";
  // An archived booklet changed since its freeze cannot be activated again: that warning blocks it.
  const issues = (check.issues || []).map((issue) => {
    if (status === "archived" && issue.code === "changed_since_freeze") return { ...issue, blocking: true };
    if (active && issue.blocking) return { ...issue, blocking: false, later: true };
    return issue;
  });
  const blocking = issues.filter((issue) => issue.blocking);
  const later = issues.filter((issue) => issue.later);
  const warnings = issues.filter(
    (issue) => !issue.blocking && !issue.later && !blocking.some((b) => b.code === issue.code && b.variant === issue.variant)
  );
  const warningsNote = warnings.length > 0 && (
    <span className="whitespace-nowrap font-normal text-amber-800"> · {t("bts.check.warnings", { count: warnings.length })}</span>
  );

  let summary;
  if (blocking.length) {
    summary = (
      <p className="flex items-start gap-2 text-sm font-medium text-red-700">
        <XCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="pt-px">
          {draft ? t("bts.check.blocked", { count: blocking.length }) : t("bts.check.problems", { count: blocking.length })}
          {warningsNote}
        </span>
      </p>
    );
  } else if (later.length) {
    summary = (
      <p className="flex items-start gap-2 text-sm font-medium text-amber-800">
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="pt-px">
          {t("bts.check.before_reactivation", { count: later.length })}
          {warningsNote}
        </span>
      </p>
    );
  } else {
    summary = (
      <p className="flex items-start gap-2 text-sm font-medium text-emerald-700">
        <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="pt-px">
          {draft ? t("bts.check.ready") : t("bts.check.ok")}
          {warningsNote}
        </span>
      </p>
    );
  }

  return (
    <section aria-labelledby="bts-check-title" className={`${cardClass} p-4 sm:p-5`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="bts-check-title" className={sectionTitleClass}>
          {t("bts.check.title")}
        </h3>
        {/* Busy, not disabled, while it reads: a disabled button would drop the keyboard focus. */}
        <QuietButton Icon={ArrowPathIcon} text={t("bts.check.refresh")} onClick={() => onRefresh()} busy={refreshing} />
      </div>
      <div className="mt-3" aria-live="polite">
        {summary}
      </div>
      <IssueList issues={[...blocking, ...later, ...warnings]} sections={check.sections} active={active} className="mt-3" />
    </section>
  );
}
